import engineApiSlice from 'src/shared/state/api-slices/engineApiSlice';
import { isConflictError } from 'src/shared/state/api-slices/engineErrors';
import type { CreateOrganisationRequest, Organisation } from 'src/shared/types/engine/organisation';

export type EnsureOrganisationOutcome = 'created' | 'active' | 'archived';

// The product licensing too: the app caches the engine's 404 for an organisation
// it does not know as `null`, which reads as "not onboarded" until refetched.
export const onboardingTags = (bcId: string) => [
    { type: 'organisation' as const, id: bcId },
    { type: 'productLicensing' as const, id: bcId },
];

/** The engine's organisations, for every product and process that onboards one. */
const organisationEngineSlice = engineApiSlice.injectEndpoints({
    endpoints: builder => ({
        /** A 409 is not a failure: the engine has the organisation already. Its name stays as the engine has it. */
        ensureOrganisation: builder.mutation<EnsureOrganisationOutcome, CreateOrganisationRequest>({
            queryFn: async (body, _queryApi, _extraOptions, baseQuery) => {
                const created = await baseQuery({ url: '/org', method: 'POST', body });
                if (created.error) {
                    if (isConflictError(created.error)) {
                        const existing = await baseQuery({ url: `/org/${body.business_central_id}`, method: 'GET' });
                        if (existing.error) {
                            return { error: existing.error };
                        } else {
                            const isActive = (existing.data as Organisation).deleted_at === null;
                            return { data: isActive ? 'active' : 'archived' };
                        }
                    } else {
                        return { error: created.error };
                    }
                } else {
                    return { data: 'created' };
                }
            },
            invalidatesTags: (_result, _error, arg) => onboardingTags(arg.business_central_id),
        }),
        /** Idempotent. The engine refuses a customer it does not know (404). */
        restoreOrganisation: builder.mutation<void, string>({
            query: bcId => ({
                url: `/org/${bcId}/restore`,
                method: 'POST',
            }),
            invalidatesTags: (_result, _error, bcId) => onboardingTags(bcId),
        }),
    }),
});

export const { useEnsureOrganisationMutation, useRestoreOrganisationMutation } = organisationEngineSlice;

export default organisationEngineSlice;
